import type {
  BatchRosterRow,
  Learner,
  LearnerAttendance,
  LearnerBatch,
  LearnerDoubt,
  LearnerHomework,
  LearnerLecture,
  LearnerOverview,
  LearnerTest,
  OwnerAcademicsOverview,
  ParentChildren,
  ParentMeeting,
  Quiz,
  QuizAttempt,
  QuizSummary,
  ReportCard,
  StudyMaterial,
  TeacherMessage,
  TimetableEntry,
} from '@tcm/types';
import { api } from '@/lib/api/client';

/** One place for every `/learning/*` call. `studentId` picks the child on the parent app; the student app omits it. */

export interface FeeItem {
  title: string;
  amountInr: number;
  dueDate: string;
}

const q = (studentId?: string) => ({ query: { studentId } });

export const learningKeys = {
  all: ['learning'] as const,
  children: ['learning', 'children'] as const,
  me: ['learning', 'me'] as const,
  overview: (id?: string) => ['learning', 'overview', id ?? 'me'] as const,
  materials: (scope: string) => ['learning', 'materials', scope] as const,
  tests: (id?: string) => ['learning', 'tests', id ?? 'me'] as const,
  homework: (id?: string) => ['learning', 'homework', id ?? 'me'] as const,
  attendance: (id?: string) => ['learning', 'attendance', id ?? 'me'] as const,
  timetable: (id?: string) => ['learning', 'timetable', id ?? 'me'] as const,
  doubts: (id?: string) => ['learning', 'doubts', id ?? 'me'] as const,
  messages: (id?: string) => ['learning', 'messages', id ?? 'me'] as const,
  meetings: (id?: string) => ['learning', 'meetings', id ?? 'me'] as const,
  fees: (id?: string) => ['learning', 'fees', id ?? 'me'] as const,
  report: (id?: string) => ['learning', 'report', id ?? 'me'] as const,
  batches: ['learning', 'batches'] as const,
  roster: (batchId: string) => ['learning', 'roster', batchId] as const,
  ownerOverview: ['learning', 'owner', 'overview'] as const,
  ownerDoubts: ['learning', 'owner', 'doubts'] as const,
  ownerTests: ['learning', 'owner', 'tests'] as const,
  ownerLectures: ['learning', 'owner', 'lectures'] as const,
  ownerQuizzes: ['learning', 'owner', 'quizzes'] as const,
  lectures: (id?: string) => ['learning', 'lectures', id ?? 'me'] as const,
  quizzes: (id?: string) => ['learning', 'quizzes', id ?? 'me'] as const,
  quiz: (quizId: string) => ['learning', 'quiz', quizId] as const,
  attempts: (id?: string) => ['learning', 'attempts', id ?? 'me'] as const,
  ownerHomework: ['learning', 'owner', 'homework'] as const,
};

export const fetchChildren = (signal?: AbortSignal) => api.get<ParentChildren>('/learning/children', { signal });
export const fetchMe = (signal?: AbortSignal) => api.get<Learner>('/learning/me', { signal });
export const fetchOverview = (id?: string, signal?: AbortSignal) => api.get<LearnerOverview>('/learning/overview', { ...q(id), signal });
export const fetchMaterials = (query: { studentId?: string; batchId?: string }, signal?: AbortSignal) => api.get<StudyMaterial[]>('/learning/materials', { query, signal });
export const fetchTests = (id?: string, signal?: AbortSignal) => api.get<LearnerTest[]>('/learning/tests', { ...q(id), signal });
export const fetchHomework = (id?: string, signal?: AbortSignal) => api.get<LearnerHomework[]>('/learning/homework', { ...q(id), signal });
export const fetchAttendance = (id?: string, signal?: AbortSignal) => api.get<LearnerAttendance>('/learning/attendance', { ...q(id), signal });
export const fetchTimetable = (id?: string, signal?: AbortSignal) => api.get<TimetableEntry[]>('/learning/timetable', { ...q(id), signal });
export const fetchDoubts = (id?: string, signal?: AbortSignal) => api.get<LearnerDoubt[]>('/learning/doubts', { ...q(id), signal });
export const fetchMessages = (id?: string, signal?: AbortSignal) => api.get<TeacherMessage[]>('/learning/messages', { ...q(id), signal });
export const fetchMeetings = (id?: string, signal?: AbortSignal) => api.get<ParentMeeting[]>('/learning/meetings', { ...q(id), signal });
export const fetchFees = (id?: string, signal?: AbortSignal) => api.get<FeeItem[]>('/learning/fees', { ...q(id), signal });
export const fetchReportCard = (id?: string, signal?: AbortSignal) => api.get<ReportCard>('/learning/report-card', { ...q(id), signal });
export const fetchBatches = (signal?: AbortSignal) => api.get<LearnerBatch[]>('/learning/batches', { signal });
export const fetchRoster = (batchId: string, signal?: AbortSignal) => api.get<BatchRosterRow[]>(`/learning/batches/${encodeURIComponent(batchId)}/roster`, { signal });
export const fetchOwnerOverview = (signal?: AbortSignal) => api.get<OwnerAcademicsOverview>('/learning/owner/overview', { signal });
export const fetchOwnerDoubts = (signal?: AbortSignal) => api.get<LearnerDoubt[]>('/learning/owner/doubts', { signal });

export const countDownload = (id: string) => api.post<{ id: string; downloads: number }>(`/learning/materials/${encodeURIComponent(id)}/download`, {});
export const submitHomework = (id: string) => api.post<LearnerHomework>(`/learning/homework/${encodeURIComponent(id)}/submit`, {});
export const askDoubt = (body: { studentId?: string; subject: string; chapter: string; question: string }) => api.post<LearnerDoubt>('/learning/doubts', body);
export const answerDoubt = (id: string, answer: string) => api.post<LearnerDoubt>(`/learning/doubts/${encodeURIComponent(id)}/answer`, { answer });
export const sendMessage = (body: { studentId?: string; body: string }) => api.post<TeacherMessage>('/learning/messages', body);
export const requestMeeting = (body: { studentId?: string; title: string; date: string; agenda: string }) => api.post<ParentMeeting>('/learning/meetings', body);
export const createMaterial = (body: Record<string, unknown>) => api.post<StudyMaterial>('/learning/materials', body);
export const assignHomework = (body: Record<string, unknown>) => api.post<{ created: string[] }>('/learning/homework', body);
export const createTest = (body: Record<string, unknown>) => api.post<{ success: boolean }>('/learning/tests', body);

export interface OwnerTestRow {
  id: string;
  batchId: string;
  batchName: string;
  title: string;
  kind: string;
  date: string;
  maxMarks: number;
  status: 'upcoming' | 'graded' | 'absent';
  subjects: string[];
  appeared?: number;
  classAverage?: number;
  topper?: number;
}

export interface OwnerHomeworkRow {
  id: string;
  batchName: string;
  subject: string;
  title: string;
  dueDate: string;
  teacher: string;
  assigned: number;
  handedIn: number;
}

export const fetchOwnerTests = (signal?: AbortSignal) => api.get<OwnerTestRow[]>('/learning/owner/tests', { signal });
export const fetchOwnerHomework = (signal?: AbortSignal) => api.get<OwnerHomeworkRow[]>('/learning/owner/homework', { signal });

export const fetchLectures = (id?: string, signal?: AbortSignal) => api.get<LearnerLecture[]>('/learning/lectures', { ...q(id), signal });
export const fetchQuizzes = (id?: string, signal?: AbortSignal) => api.get<QuizSummary[]>('/learning/quizzes', { ...q(id), signal });
export const fetchQuiz = (quizId: string, signal?: AbortSignal) => api.get<Quiz>(`/learning/quizzes/${encodeURIComponent(quizId)}`, { signal });
export const fetchAttempts = (id?: string, signal?: AbortSignal) => api.get<QuizAttempt[]>('/learning/quiz-attempts', { ...q(id), signal });
export const submitQuiz = (quizId: string, answers: (number | null)[]) => api.post<QuizAttempt>(`/learning/quizzes/${encodeURIComponent(quizId)}/submit`, { answers });
export const saveLectureProgress = (id: string, percent: number) => api.post<LearnerLecture>(`/learning/lectures/${encodeURIComponent(id)}/progress`, { percent });

export interface OwnerLectureRow extends LearnerLecture {
  batchId: string;
  batchName: string;
}

export interface OwnerQuizRow {
  id: string;
  batchId: string;
  batchName: string;
  title: string;
  subject: string;
  chapter: string;
  questionCount: number;
  durationMinutes: number;
  attempts: number;
  averagePercent?: number;
}

export interface NewQuizQuestion {
  topic: string;
  text: string;
  options: string[];
  correctIndex: number;
  explanation: string;
}

export const fetchOwnerLectures = (signal?: AbortSignal) => api.get<OwnerLectureRow[]>('/learning/owner/lectures', { signal });
export const fetchOwnerQuizzes = (signal?: AbortSignal) => api.get<OwnerQuizRow[]>('/learning/owner/quizzes', { signal });
export const scheduleLecture = (body: Record<string, unknown>) => api.post<{ created: number }>('/learning/lectures', body);
export const createQuiz = (body: Record<string, unknown>) => api.post<{ id: string; questionCount: number }>('/learning/quizzes', body);
